import { useRef, type KeyboardEvent } from "react";
import { useTranslations } from "next-intl";
import { PillToggle } from "@/components/PillToggle/PillToggle";
import { CheckboxWithValidation } from "@/components/Inputs/CheckboxWithValidation";
import { SEND_CLASSES, normalizeSendClass, type PickableSendClass, type SendClass } from "@/lib/email/sendClass";

interface SendClassPickerProps {
  value: SendClass;
  attested: boolean;
  onChange: (next: { sendClass: SendClass; attested: boolean }) => void;
  /**
   * Id for the "confirm first" line, so a send button locked by the missing
   * tick can point at it via `aria-describedby`.
   */
  attestationHintId?: string;
  /**
   * A send/save was attempted without the tick. Only then is the box marked
   * invalid — red line, `aria-invalid`, `aria-describedby` — never merely for
   * switching to the organisational class.
   */
  showValidation?: boolean;
  disabled?: boolean;
  /**
   * Classes that cannot be picked while the rest of the group stays usable —
   * the backend's switch is off, so an owner may still move a stored
   * organisational notice to marketing (B2-3), never back.
   */
  disabledClasses?: readonly SendClass[];
  /**
   * Shown instead of the attestation controls when the viewer may not change
   * the class (a stored organisational notice seen by an editor).
   */
  lockedNotice?: string;
  testIdPrefix?: string;
}

/**
 * "Marketing / newsletter" vs "Organisational notice (not marketing)", plus
 * the mandatory attestation an organisational send needs. Shared by the three
 * send dialogs and the scheduled-workflow action editor; the CALLER decides
 * whether the current role may see it at all (`canChooseSendClass`).
 *
 * The tick is never pre-set and is dropped whenever the class changes, so an
 * organisational send always carries a fresh, deliberate attestation.
 */
export function SendClassPicker({
  value,
  attested,
  onChange,
  attestationHintId,
  showValidation = false,
  disabled = false,
  disabledClasses = [],
  lockedNotice,
  testIdPrefix = "send-class",
}: SendClassPickerProps) {
  const t = useTranslations("pSendEmailValidation");
  const organisational = value === "ORGANISATIONAL";
  const hintId = attestationHintId ?? `${testIdPrefix}-attestation-hint`;
  const showMissing = showValidation && organisational && !attested;
  // Keyed by the PICKABLE classes only: STAFF_NOTICE (W4-3) is a row class the
  // consumer writes on arrival notices, never a radio here.
  const radioRefs = useRef<Record<PickableSendClass, HTMLButtonElement | null>>({
    MARKETING: null,
    ORGANISATIONAL: null,
  });

  const classDisabled = (cls: SendClass) => disabled || disabledClasses.includes(cls);
  // The tab stop is the checked radio — unless that one is locked (a disabled
  // button takes no focus), then the first pickable one.
  const tabStop: SendClass | undefined = !classDisabled(value)
    ? value
    : SEND_CLASSES.find((cls) => !classDisabled(cls));

  const pick = (next: SendClass) => {
    if (classDisabled(next)) return;
    onChange({
      sendClass: next,
      // Re-picking the active class keeps the tick; switching starts unticked.
      attested: next === value ? attested : false,
    });
  };

  // WAI-ARIA radio group: one tab stop (the checked radio), arrow keys move
  // the selection AND the focus, wrapping at both ends.
  const onRadioKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const step =
      e.key === "ArrowRight" || e.key === "ArrowDown"
        ? 1
        : e.key === "ArrowLeft" || e.key === "ArrowUp"
          ? -1
          : 0;
    if (step === 0 || disabled) return;
    e.preventDefault();
    // Walk past locked classes; a full lap without a pickable one is a no-op.
    const n = SEND_CLASSES.length;
    // A non-pickable value (STAFF_NOTICE) normalises to MARKETING, so the walk
    // always starts on a real radio.
    const i = SEND_CLASSES.indexOf(normalizeSendClass(value));
    for (let k = 1; k < n; k++) {
      const next = SEND_CLASSES[(((i + step * k) % n) + n) % n];
      if (classDisabled(next)) continue;
      pick(next);
      radioRefs.current[next]?.focus();
      return;
    }
  };

  return (
    <div className="flex flex-col gap-2" data-testid={testIdPrefix}>
      <p className="body-2-medium font-primary" id={`${testIdPrefix}-label`}>
        {t("sendClassLabel")}
      </p>
      <div
        role="radiogroup"
        aria-labelledby={`${testIdPrefix}-label`}
        className="flex flex-wrap gap-2"
      >
        {SEND_CLASSES.map((cls) => (
          <PillToggle
            key={cls}
            role="radio"
            label={
              cls === "ORGANISATIONAL"
                ? t("sendClassOrganisational")
                : t("sendClassMarketing")
            }
            active={value === cls}
            disabled={classDisabled(cls)}
            tabIndex={tabStop === cls ? 0 : -1}
            onKeyDown={onRadioKeyDown}
            buttonRef={(el) => {
              radioRefs.current[cls] = el;
            }}
            onClick={() => pick(cls)}
          />
        ))}
      </div>
      <p className="body-3-regular font-tertiary">
        {organisational
          ? t("sendClassOrganisationalHint")
          : t("sendClassMarketingHint")}
      </p>
      {lockedNotice && (
        <p
          role="note"
          className="body-3-medium font-secondary"
          data-testid={`${testIdPrefix}-locked`}
        >
          {lockedNotice}
        </p>
      )}
      {organisational && !lockedNotice && (
        <div className="flex flex-col gap-1">
          <CheckboxWithValidation
            isChecked={attested}
            isInvalid={showMissing}
            ariaRequired
            ariaInvalid={showMissing}
            ariaDescribedBy={showMissing ? hintId : undefined}
            disabled={disabled}
            alignStart
            onCheckChangeAction={(checked) =>
              onChange({ sendClass: "ORGANISATIONAL", attested: checked })
            }
            testId={`${testIdPrefix}-attestation`}
          >
            <span className="body-3-medium font-primary">
              {t("sendClassAttestation")}
            </span>
          </CheckboxWithValidation>
          {showMissing && (
            <p
              id={hintId}
              className="body-3-regular font-error"
              data-testid={`${testIdPrefix}-attestation-required`}
            >
              {t("sendClassAttestationRequired")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
